/**
 * BK · Build Your Own Bundle — headless Appstle bundle builder
 * assets/bk-bundle-builder.js
 *
 * Vanilla JS, no framework. Drives sections/bk-bundle-builder.liquid.
 *
 * P4b motion: GSAP + ScrollTrigger are an ENHANCEMENT, not a dependency. The page
 * behaves identically when window.gsap is undefined, when the merchant turns the
 * "Enable GSAP motion" setting off, or when prefers-reduced-motion is set — every
 * state is then applied instantly by the same render code. GSAP core (3.12.5) is
 * loaded once per page by sections/bk-hp-header.liquid; the section loads only the
 * matching ScrollTrigger plugin. All motion is transform/opacity except the two
 * height collapses the brief asked for (summary row removal, FAQ answer).
 * Docs D-07…D-12 in docs/build-a-bundle/P4b-build-report.md.
 *
 * Contract with Appstle (proven live in P2 spike):
 *   - The discount is applied by a Shopify Function that reads THREE line item
 *     properties. There is no "discount call". Keys are matched exactly — the
 *     mixed hyphen/underscore and single/double leading underscore are load-bearing.
 *   - Tier counts UNITS (quantity), not distinct products.
 *   - The Function validates pool membership server-side; our pool list is UX only.
 *   - PRICES never come from the rule (P4g): the rule's price is kept only as rulePricePence for the
 *     report-only diff; pricePence starts null and is filled from the pool collection map or
 *     /products/{handle}.js. A card shows no price (and cannot be added) until live data arrives.
 *   - The discount is line-level. Read total_discount / line_level_discount_allocations,
 *     never cart_level_discount_applications (always empty for this discount).
 *   - The Function needs ~1s to settle after /cart/add.js. Poll with backoff.
 *
 * P4d hand-off: once the titled allocation is verified the bundle is handed to the
 * store's cart UI — Rebuy Smart Cart — via its documented JS API (D-14:
 * window.Rebuy.SmartCart.show(), .visible, .status === 'ready', document events
 * rebuy:smartcart.ready / show / hide). Rebuy hooks /cart/add.js and may open the
 * drawer itself; then we do not open it twice. Without the API the page goes to
 * routes.cart_url. The builder NEVER navigates to /checkout.
 *
 * P4d.1 dynamic labels: nothing about the tier ladder is hard-coded in copy. After the rule is
 * normalised, applyRuleTokens() fills the rule-level tokens {min} {max} {min_pct} {max_pct} {tiers}
 * in every label, the SEO fallback strings and every [data-bb-dyn] element (intro, note, empty
 * state, FAQ answers). Per-tier copy (ladder rows, the FAQ tier sentence) uses one template with
 * the runtime tokens {n} {pct}; the top tier renders "{n}+" in the ladder.
 *
 * Docs (D-numbers in docs/build-a-bundle/P4a-build-report.md):
 *   D-05 Cart Ajax API — /cart/add.js, /cart.js, /cart/change.js, prices in minor units.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Frozen constants — DO NOT EDIT without re-running the live proof.   */
  /* ------------------------------------------------------------------ */
  var APPSTLE_KEYS = Object.freeze({
    id: '_appstle-bb-id',            // one leading underscore, hyphens
    type: '_appstle_bundles_type',   // one leading underscore, underscores
    name: '__appstle-bb-name'        // TWO leading underscores, hyphens
  });

  var VERIFY_DELAYS_MS = Object.freeze([250, 400, 550, 700, 850]); // cumulative 2750ms < 3s cap
  var GIFT_POLL_MS = 400;                                               // P4d: report-only free-gift probe interval …
  var GIFT_DEADLINE_MS = 3000;                                          // … under an absolute 3s deadline from the hand-off
  var REBUY_READY_WAIT_MS = 1500;                                       // P4d: wait for rebuy:smartcart.ready this long before falling back to the cart page
  var FETCH_TIMEOUT_MS = 8000;
  var STOCK_BATCH = 6;
  var UNLOCK_MS = 1400;
  var URL_PARAM = 'b';

  /* P4b motion timings (seconds, GSAP). Brief values; keep in one place. */
  var MOTION = Object.freeze({
    unlockScale: 0.32,   // ladder row 1 → 1.04 → 1
    fill: 0.45,          // progress fill scaleX
    pct: 0.4,            // mobile bar percentage count (snap 1)
    rowIn: 0.24,         // new summary row fade + y:8
    money: 0.35,         // totals count (snap 1 pence)
    badge: 0.2,          // card badge 1 → 1.08 → 1
    rowOut: 0.22,        // summary row collapse
    reveal: 0.45,        // scroll reveal fade-up from y:16
    revealStagger: 0.05,
    faq: 0.26,           // accordion height/opacity
    busy: 0.5,           // checkout pulse half-cycle; repeat 5 → 3s cap
    refreshDebounce: 150 // ms, ScrollTrigger.refresh()
  });

  /* ------------------------------------------------------------------ */
  /* Small utilities                                                      */
  /* ------------------------------------------------------------------ */
  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function on(el, ev, fn) { if (el) el.addEventListener(ev, fn); }
  function text(el, str) { if (el) el.textContent = str == null ? '' : String(str); }
  function show(el, yes) { if (el) el.hidden = !yes; }
  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /** Appstle gives "30.00" (string, major units). Cart gives 3000 (int, minor). Normalise once, here. */
  function majorToPence(v) {
    var n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    if (!isFinite(n)) return 0;
    return Math.round(n * 100);
  }

  /** Per-line rounding that matched the live Function (42.5 → 42). Still an estimate. */
  function roundHalfDown(x) { return Math.floor(x + 0.5 - 1e-9); }

  function parseMaybeJSON(v) {
    if (typeof v !== 'string') return v;
    var t = v.trim();
    if (!t) return null;
    try { return JSON.parse(t); } catch (e) { return null; }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  function fill(template, map) {
    var out = String(template || '');
    Object.keys(map).forEach(function (k) { out = out.split('{' + k + '}').join(String(map[k])); });
    return out;
  }

  /** "Add {n} more item" → pluralise "item" when n !== 1 (and never double-pluralise). */
  function pluralise(str, n, one, many) {
    if (n === 1) return str;
    return str.replace(new RegExp('\\b' + one + '\\b(?!s)', 'g'), many);
  }

  function hashOf(str) {
    var h = 0, i;
    for (i = 0; i < str.length; i++) { h = ((h << 5) - h + str.charCodeAt(i)) | 0; }
    return String(h);
  }

  function stripSizeFromTitle(title) {
    return String(title || '')
      .replace(/\s*\((?:travel|refill|web|retail sku)\)\s*/ig, ' ')
      .replace(/\s*[-–]?\s*\b\d+(?:\.\d+)?\s?(?:ml|g|kg|l)\b\.?/ig, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function sizeLabel(item) {
    var vt = item.variantTitle;
    if (vt && vt !== 'Default Title') return vt;
    var m = /(\d+(?:\.\d+)?)\s?(ml|g|kg|l)\b/i.exec(item.title || '');
    if (m) return m[1] + m[2].toLowerCase();
    return 'One size';
  }

  /* ------------------------------------------------------------------ */
  /* Builder                                                              */
  /* ------------------------------------------------------------------ */
  function BundleBuilder(root) {
    this.root = root;
    this.sid = root.getAttribute('data-section-id');
    this.token = root.getAttribute('data-token');
    this.bundleType = root.getAttribute('data-bundle-type');
    this.bundleName = root.getAttribute('data-bundle-name');
    this.discountTitle = root.getAttribute('data-discount-title');
    this.configSource = root.getAttribute('data-config-source') || 'metafield_then_proxy';
    this.proxyPath = root.getAttribute('data-proxy-path') || '/apps/bundles/bb/api/build-a-box/by-token/';
    this.routesRoot = (root.getAttribute('data-routes-root') || '/').replace(/\/?$/, '/');
    this.cartUrl = root.getAttribute('data-cart-url') || (this.routesRoot.replace(/\/$/, '') + '/cart');   // P4d fallback target (routes.cart_url)
    this.currency = root.getAttribute('data-currency') || 'GBP';
    this.oosBehaviour = root.getAttribute('data-oos-behaviour') || 'disable';
    this.showEffective = root.getAttribute('data-show-effective-price') === 'true';
    this.enableStepper = root.getAttribute('data-enable-stepper') !== 'false';
    this.enableSuggestions = root.getAttribute('data-enable-suggestions') === 'true';
    this.showSuggestions = root.getAttribute('data-show-suggestions') === 'true';   // P4f master switch; off = no suggestion logic at all
    this.sortMode = root.getAttribute('data-sort-mode') || 'appstle';                 // P4g: appstle | title | price_asc
    this.showLadderFull = root.getAttribute('data-show-ladder-full') !== 'false';

    var settingsEl = qs(root, '[data-bk-bb-settings]');
    this.settings = parseMaybeJSON(settingsEl ? settingsEl.textContent : '{}') || {};
    this.labels = this.settings.labels || {};

    this.fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.currency });

    this.config = null;          // normalised rule
    this.items = [];             // pool items
    this.byVariant = {};         // variantId → item
    this.byHandle = {};          // handle → item
    this.cards = [];             // grouped cards
    this.sel = new Map();        // variantId → qty (units)
    this.inflight = null;        // payload hash while submitting
    this.lastTierIdx = -1;
    this.unlockTimer = null;
    this.hydratedFromUrl = false;
    this.filter = 'all';
    this.confirmedSaving = null; // pence, after reconcile
    this.stockSource = 'none';
    this.configSourceUsed = 'none';
    this.motion = null;          // P4b: set by initMotion() when GSAP is present and enabled
    this.destroyed = false;      // set by destroy() on shopify:section:unload
    this.collapsing = null;      // summary row mid-collapse (renderItems waits for it)
    this.collapseTween = null;
    this.collapseTimer = null;
    this.visKey = '';            // visible card keys — reveals are flushed when this changes
    this.rebuyShownDuringSubmit = false; // P4d: set by rebuy:smartcart.show while a submit is in flight (Codex P4d #6: per-submit, not wall-clock)
    this.cancelHandoff = null;   // P4d: cancels a pending ready-wait on destroy (Codex P4d #4)
    this.handoff = null;         // P4d: 'rebuy-opened' | 'already-open' | 'navigate' after the last successful submit

    this.tpl = {};
    qsa(root, 'template[data-bk-bb-tpl]').forEach(function (t) { this.tpl[t.getAttribute('data-bk-bb-tpl')] = t; }.bind(this));

    this.el = {
      resume: qs(root, '[data-resume]'),
      resumeYes: qs(root, '[data-resume-yes]'),
      resumeNo: qs(root, '[data-resume-no]'),
      mobilebar: qs(root, '[data-mobilebar]'),
      barPct: qs(root, '[data-mobilebar] [data-pct]'),
      barMsg: qs(root, '[data-bar-msg]'),
      barFill: qs(root, '[data-mobilebar] [data-fill]'),
      barStops: qs(root, '[data-bar-stops]'),
      filters: qs(root, '[data-filters]'),
      filterSelect: qs(root, '[data-filter-select]'),
      tierStrip: qs(root, '[data-tier-strip]'),
      tierStripList: qs(root, '[data-tier-strip-list]'),
      loading: qs(root, '[data-loading]'),
      configError: qs(root, '[data-config-error]'),
      grid: qs(root, '[data-grid]'),
      gridEmpty: qs(root, '[data-grid-empty]'),
      rail: qs(root, '[data-rail]'),
      sheetToggle: qs(root, '[data-sheet-toggle]'),
      sheetSummary: qs(root, '[data-sheet-summary]'),
      railCount: qs(root, '[data-rail-count]'),
      ladder: qs(root, '[data-ladder]'),
      tierMsg: qs(root, '[data-tier-msg]'),
      suggest: qs(root, '[data-suggest]'),
      suggestLabel: qs(root, '[data-suggest-label]'),
      suggestList: qs(root, '[data-suggest-list]'),
      items: qs(root, '[data-items]'),
      empty: qs(root, '[data-empty]'),
      subtotal: qs(root, '[data-subtotal]'),
      savingLabel: qs(root, '[data-saving-label]'),
      saving: qs(root, '[data-saving]'),
      total: qs(root, '[data-total]'),
      estimateNote: qs(root, '[data-estimate-note]'),
      checkout: qs(root, '[data-checkout]'),
      checkoutLabel: qs(root, '[data-checkout-label]'),
      ctaReason: qs(root, '[data-cta-reason]'),
      submitError: qs(root, '[data-submit-error]'),
      submitErrorText: qs(root, '[data-submit-error-text]'),
      retry: qs(root, '[data-retry]'),
      clear: qs(root, '[data-clear]'),
      scrim: qs(root, '[data-scrim]'),
      live: qs(root, '[data-live]')
    };

    this.init();
  }

  var P = BundleBuilder.prototype;

  /* ---------------- init ---------------- */
  P.init = function () {
    var self = this;
    this.bindStatic();
    this.measureHeader();

    this.loadConfig().then(function (ok) {
      if (self.destroyed) return;   // section unloaded (theme editor) while the rule was loading
      if (!ok) {
        show(self.el.loading, false);
        text(self.el.configError, self.labels.errorConfig);
        show(self.el.configError, true);
        self.hideUnresolvedDynamic();
        return;
      }
      self.applyRuleTokens();       // P4d.1: labels / SEO / [data-bb-dyn] copy derive from the rule
      self.applySeoFallback();
      self.buildIndexes();
      self.buildCards();
      self.renderGrid();
      self.renderLadderSkeleton();
      self.renderTierStrip();       // P4g
      show(self.el.loading, false);

      self.hydrateState();          // ?b= then sessionStorage
      self.render();
      self.initMotion();            // P4b: after the first (instant) render, before stock re-renders
      self.resolveStock();          // async; re-renders cards as batches land
      self.checkCartForResume();    // async; may show the prompt
    });
  };

  /** Theme editor: shopify:section:unload → kill every tween/ScrollTrigger and listener we own. */
  P.destroy = function () {
    this.destroyed = true;          // an in-flight loadConfig() must not re-render or re-init motion afterwards
    clearTimeout(this.unlockTimer);
    this.finishCollapse();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    if (this.onRebuyShow) document.removeEventListener('rebuy:smartcart.show', this.onRebuyShow);
    if (this.cancelHandoff) this.cancelHandoff();
    if (this.motion) {
      this.motion.mm.revert();      // D-08/D-12: reverts the context (reveal set + ScrollTriggers) and runs our cleanup
      this.motion = null;
    }
  };

  P.bindStatic = function () {
    var self = this;
    on(this.el.checkout, 'click', function () { self.submit(); });
    on(this.el.retry, 'click', function () { self.hideSubmitError(); self.submit(); });
    on(this.el.clear, 'click', function () { self.sel.clear(); self.confirmedSaving = null; self.persist(); self.render(); self.announce(self.labels.announceNone); });
    on(this.el.resumeYes, 'click', function () { self.resume(); });
    on(this.el.resumeNo, 'click', function () { self.startOver(); });
    on(this.el.sheetToggle, 'click', function () { self.toggleSheet(); });
    on(this.el.scrim, 'click', function () { self.toggleSheet(false); });
    this.onKey = function (e) { if (e.key === 'Escape' && self.el.rail && self.el.rail.classList.contains('is-open')) self.toggleSheet(false); };
    on(document, 'keydown', this.onKey);
    this.bindFaq();

    // P4d: one labelled native <select> replaces the pill row (same filter values: 'all' | block.id).
    if (this.el.filterSelect) {
      on(this.el.filterSelect, 'change', function () {
        self.filter = self.el.filterSelect.value || 'all';
        self.applyFilter();
      });
    }

    // P4d: Rebuy opening its own drawer during our add is detected via the documented show event (D-14).
    this.onRebuyShow = function () { if (self.inflight) self.rebuyShownDuringSubmit = true; };
    document.addEventListener('rebuy:smartcart.show', this.onRebuyShow);
    this.ctaIdleLabel = this.el.checkoutLabel ? this.el.checkoutLabel.textContent : '';

    qsa(this.root, '[data-preset]').forEach(function (btn) {
      on(btn, 'click', function () { self.applyPreset(btn.getAttribute('data-preset')); });
    });

    this.onResize = function () { self.measureHeader(); self.motionRefresh(); };
    window.addEventListener('resize', this.onResize, { passive: true });
  };

  /**
   * The theme's header is not exposed as a CSS variable. Live (3 Sep 2026) it is
   * `.bk-hp-header.is-sticky` (124px, position: sticky); the Eurus default is
   * `#sticky-header-content`. Take the first candidate that is actually sticky/fixed.
   */
  P.measureHeader = function () {
    var h = 0;
    var candidates = qsa(document, '.bk-hp-header, #sticky-header-content, .shopify-section-group-header-group header, header.header, .header-wrapper');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var cs = getComputedStyle(el);
      if ((cs.position === 'sticky' || cs.position === 'fixed') && el.offsetParent !== null) {
        var rect = el.getBoundingClientRect();
        if (rect.height > 0) { h = rect.height; break; }
      }
    }
    this.root.style.setProperty('--bk-bb-header-offset', Math.round(h) + 'px');
  };

  P.applySeoFallback = function () {
    var seo = this.settings.seo || {};
    if (seo.title) document.title = seo.title;
    if (seo.description) {
      var m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
      m.setAttribute('content', seo.description);
    }
  };

  /**
   * P4d.1 — rule-level tokens. {min}/{max} are the unit bounds, {min_pct}/{max_pct} the first/top tier
   * percentages, {tiers} the sentence "2 items 5%, 3 items 10%, 4 items 15%, 5 or more 20%" built from
   * the tierSentence / tierSentenceLast templates. Runtime tokens ({n}, {pct}, {total}, …) are untouched.
   */
  P.ruleTokens = function () {
    var tiers = (this.config && this.config.tiers) || [], L = this.labels;
    var base = {
      min: this.config.min,
      max: this.config.max,
      min_pct: tiers.length ? tiers[0].pct : 0,
      max_pct: tiers.length ? tiers[tiers.length - 1].pct : 0
    };
    // Sentence templates may themselves use the numeric rule tokens; fill those first, then {n}/{pct} per tier (Codex P4d.1 #2).
    var each = fill(L.tierSentence || '{n} items {pct}%', base);
    var lastTpl = fill(L.tierSentenceLast || L.tierSentence || '{n} or more {pct}%', base);
    base.tiers = tiers.map(function (t, i) {
      return fill(i === tiers.length - 1 ? lastTpl : each, { n: t.units, pct: t.pct });
    }).join(', ');
    return base;
  };

  /** Config failed: never show raw tokens — hide any dynamic copy that still carries one (Codex P4d.1 #4). */
  P.hideUnresolvedDynamic = function () {
    qsa(this.root, '[data-bb-dyn]').forEach(function (el) {
      if (/\{(min|max|min_pct|max_pct|tiers)\}/.test(el.innerHTML)) el.hidden = true;
    });
  };

  P.applyRuleTokens = function () {
    var self = this;
    var tokens = this.ruleTokens();
    this.tokens = tokens;
    Object.keys(this.labels).forEach(function (k) { if (typeof self.labels[k] === 'string') self.labels[k] = fill(self.labels[k], tokens); });
    var seo = this.settings.seo || {};
    Object.keys(seo).forEach(function (k) { if (typeof seo[k] === 'string') seo[k] = fill(seo[k], tokens); });
    // Merchant richtext: only the token values are inserted; the sentence is escaped because it is built from label text.
    var htmlTokens = Object.assign({}, tokens, { tiers: escapeHtml(tokens.tiers) });
    qsa(this.root, '[data-bb-dyn]').forEach(function (el) {
      if (/\{(min|max|min_pct|max_pct|tiers)\}/.test(el.innerHTML)) el.innerHTML = fill(el.innerHTML, htmlTokens);
    });
  };

  P.announce = function (msg) {
    var el = this.el.live;
    if (!el || !msg) return;
    text(el, '');
    setTimeout(function () { text(el, msg); }, 30);
  };

  /* ---------------- config ---------------- */
  P.loadConfig = function () {
    var self = this;
    var fromMetafield = null;
    if (this.configSource !== 'proxy') {
      var el = qs(this.root, '[data-bk-bb-config]');
      if (el) fromMetafield = this.extractRule(parseMaybeJSON(el.textContent));
    }
    if (fromMetafield && this.isFreshRule(fromMetafield)) {
      this.config = this.normaliseRule(fromMetafield);
      this.configSourceUsed = 'metafield';
      return Promise.resolve(true);
    }
    if (this.configSource === 'metafield') return Promise.resolve(false);

    var url = this.proxyPath.replace(/\/?$/, '/') + encodeURIComponent(this.token);
    return this.fetchJSON(url, { retries: 2 }).then(function (rule) {
      var r = self.extractRule(rule);
      if (!r || !self.isFreshRule(r)) return false;
      self.config = self.normaliseRule(r);
      self.configSourceUsed = 'proxy';
      return true;
    }).catch(function () { return false; });
  };

  /** Accepts: a rule object; an array of rules; an object wrapping rules; a JSON string of any of those. */
  P.extractRule = function (data) {
    var token = this.token;
    data = parseMaybeJSON(data);
    if (!data || typeof data !== 'object') return null;
    if (Array.isArray(data)) {
      for (var i = 0; i < data.length; i++) { var r = this.extractRule(data[i]); if (r) return r; }
      return null;
    }
    if (data.uniqueRef === token) return data;
    var keys = ['rules', 'bundleRules', 'bundles', 'data', 'items'];
    for (var k = 0; k < keys.length; k++) {
      if (data[keys[k]]) { var rr = this.extractRule(data[keys[k]]); if (rr) return rr; }
    }
    if (data[token]) return this.extractRule(data[token]);
    return null;
  };

  /** "Stale" = not ACTIVE, or missing the tier ladder / pool we need to render. */
  P.isFreshRule = function (rule) {
    if (!rule) return false;
    if (rule.status && String(rule.status).toUpperCase() !== 'ACTIVE') return false;
    var tiers = parseMaybeJSON(rule.tieredDiscount);
    var variants = parseMaybeJSON(rule.variants);
    return Array.isArray(tiers) && tiers.length > 0 && Array.isArray(variants) && variants.length > 0;
  };

  P.normaliseRule = function (rule) {
    var tiers = (parseMaybeJSON(rule.tieredDiscount) || [])
      .filter(function (t) { return String(t.discountBasedOn || 'QUANTITY').toUpperCase() === 'QUANTITY' && String(t.discountType || 'PERCENTAGE').toUpperCase() === 'PERCENTAGE'; })
      .map(function (t) { return { units: parseInt(t.value, 10), pct: parseFloat(t.discount) }; })
      .filter(function (t) { return isFinite(t.units) && isFinite(t.pct); })
      .sort(function (a, b) { return a.units - b.units; });

    var items = (parseMaybeJSON(rule.variants) || []).map(function (v, idx) {
      return {
        ruleIndex: idx,                                   // P4g: position in the Appstle rule (the drag order) — the default grid order
        variantId: String(v.variantId),
        productId: String(v.productId),
        handle: String(v.productHandle || ''),
        title: String(v.productTitle || v.name || ''),
        variantTitle: v.variantTitle == null ? null : String(v.variantTitle),
        image: String(v.imageSrc || ''),
        rulePricePence: majorToPence(v.price),   // report-only (P4g price diff); never rendered
        pricePence: null,         // live price — pool collection map or /products/{handle}.js
        available: null,          // unknown until stock resolves
        range: 'other'
      };
    }).filter(function (i) { return i.variantId && i.variantId !== 'undefined'; });

    var min = parseInt(rule.minProductCount, 10);
    var max = parseInt(rule.maxProductCount, 10);
    return {
      token: rule.uniqueRef || this.token,
      type: rule.bundleType || this.bundleType,
      name: rule.name || this.bundleName,
      discountName: rule.discountName || this.discountTitle,
      min: isFinite(min) && min > 0 ? min : (tiers[0] ? tiers[0].units : 2),
      max: isFinite(max) && max > 0 ? max : 10,
      tiers: tiers,
      items: items
    };
  };

  /** fetch with a hard timeout so a hanging connection can never leave the CTA busy forever. */
  function fetchWithTimeout(url, init, ms) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, ms || FETCH_TIMEOUT_MS) : null;
    return fetch(url, Object.assign({}, init, ctrl ? { signal: ctrl.signal } : {})).then(function (res) { clearTimeout(timer); return res; }, function (e) { clearTimeout(timer); throw e; });
  }

  P.fetchJSON = function (url, opts) {
    opts = opts || {};
    var retries = opts.retries || 0;
    var attempt = function (n) {
      return fetchWithTimeout(url, { method: opts.method || 'GET', headers: Object.assign({ 'Accept': 'application/json' }, opts.headers || {}), body: opts.body, credentials: 'same-origin' }, opts.timeout)
        .then(function (res) {
          if (!res.ok) { var err = new Error('HTTP ' + res.status); err.status = res.status; err.res = res; throw err; }
          return res.json();
        })
        .catch(function (e) { if (n > 0 && (!e.status || e.status >= 500)) return sleep(300).then(function () { return attempt(n - 1); }); throw e; });
    };
    return attempt(retries);
  };

  /* ---------------- indexes, ranges, cards ---------------- */
  P.buildIndexes = function () {
    var self = this;
    this.items = this.config.items;
    this.byVariant = {}; this.byHandle = {};
    this.items.forEach(function (i) { self.byVariant[i.variantId] = i; self.byHandle[i.handle] = i; });

    // Range = first filter group whose handle list contains the item (documented mapping in the build report).
    this.rangeLabels = {};
    (this.settings.filterGroups || []).forEach(function (g) {
      self.rangeLabels[g.id] = g.label;
      String(g.handles || '').split(/\r?\n|,/).map(function (h) { return h.trim(); }).filter(Boolean).forEach(function (h) {
        var it = self.byHandle[h];
        if (it && it.range === 'other') it.range = g.id;
      });
    });
  };

  /**
   * P4g: cards render in the Appstle rule's order (items[].ruleIndex, i.e. the drag order under
   * "Discount applies to → Specific products"). A size-grouped card takes the position of its first
   * member in that order; its size options keep the size_group_map order (first listed = default size).
   * sort_mode "title" / "price_asc" re-sort the finished cards; filters never re-order (applyFilter only hides).
   */
  P.buildCards = function () {
    var self = this;
    var groupOf = {};      // variantId → group record
    var groups = [];
    String(this.settings.sizeGroups || '').split(/\r?\n/).forEach(function (line, idx) {
      var handles = line.split('|').map(function (h) { return h.trim(); }).filter(Boolean);
      // A handle repeated within the line, or already claimed by an earlier line, is ignored (Codex P4g A2) — one card per product.
      var opts = [];
      handles.forEach(function (h) { var it = self.byHandle[h]; if (it && !groupOf[it.variantId] && opts.indexOf(it) < 0) opts.push(it); });
      if (opts.length < 1) return;
      var g = { key: 'g' + idx, options: opts, placed: false };
      groups.push(g);
      opts.forEach(function (o) { groupOf[o.variantId] = g; });
    });
    this.cards = [];
    this.items.forEach(function (i) {           // this.items is in rule order (normaliseRule keeps it)
      var g = groupOf[i.variantId];
      if (g) {
        if (g.placed) return;                     // later members of an already-placed group
        g.placed = true;
        self.cards.push({ key: g.key, title: stripSizeFromTitle(g.options[0].title), options: g.options, range: g.options[0].range, grouped: g.options.length > 1, order: i.ruleIndex });
        return;
      }
      self.cards.push({ key: 'v' + i.variantId, title: i.title, options: [i], range: i.range, grouped: false, order: i.ruleIndex });
    });
    if (this.sortMode === 'title') this.cards.sort(function (a, b) { return a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) || a.order - b.order; });
    else if (this.sortMode === 'price_asc') this.cards.sort(function (a, b) { var pa = a.options[0].pricePence == null ? Infinity : a.options[0].pricePence, pb = b.options[0].pricePence == null ? Infinity : b.options[0].pricePence; return (pa - pb) || a.order - b.order; });
    else this.cards.sort(function (a, b) { return a.order - b.order; });
  };

  P.clone = function (name) {
    var t = this.tpl[name];
    return t ? t.content.firstElementChild.cloneNode(true) : null;
  };

  P.renderGrid = function () {
    var self = this;
    var grid = this.el.grid;
    if (!grid) return;
    grid.innerHTML = '';
    this.cards.forEach(function (card) {
      var node = self.clone('card');
      if (!node) return;
      card.el = node;
      card.active = card.options[0]; // default size = first listed
      node.setAttribute('data-range', card.range);
      node.setAttribute('data-card-key', card.key);
      text(qs(node, '[data-title]'), card.title);
      var img = qs(node, '[data-img]');
      var sizes = qs(node, '[data-sizes]');
      if (card.grouped) {
        sizes.setAttribute('aria-label', 'Size: ' + card.title);
        card.options.forEach(function (opt) {
          var b = self.clone('size');
          b.setAttribute('data-variant', opt.variantId);
          text(b, sizeLabel(opt));
          on(b, 'click', function () { self.setActiveSize(card, opt); });
          sizes.appendChild(b);
        });
        show(sizes, true);
      }
      on(qs(node, '[data-add]'), 'click', function () { self.changeQty(card.active.variantId, 1, node); });
      on(qs(node, '[data-inc]'), 'click', function () { self.changeQty(card.active.variantId, 1, node); });
      on(qs(node, '[data-dec]'), 'click', function () { self.changeQty(card.active.variantId, -1, node); });
      if (img) { img.src = card.active.image; img.alt = ''; }
      grid.appendChild(node);
    });
    this.pruneFilterOptions();
    this.applyFilter();
  };

  /**
   * P4d / item 5: a Filter group whose handles are all missing from the Appstle rule (product removed
   * in Appstle, typo, retired handle) would only ever show the empty message — hide that option.
   * Pool products in no group keep range 'other' and appear under "All ranges" only.
   */
  P.pruneFilterOptions = function () {
    var sel = this.el.filterSelect;
    if (!sel) return;
    var counts = {};
    this.cards.forEach(function (c) { counts[c.range] = (counts[c.range] || 0) + 1; });
    qsa(sel, 'option').forEach(function (o) {
      if (o.value === 'all') return;
      var empty = !counts[o.value];
      o.hidden = empty;
      o.disabled = empty;
    });
    if (this.filter !== 'all' && !counts[this.filter]) { this.filter = 'all'; sel.value = 'all'; }
  };

  P.setActiveSize = function (card, opt) {
    card.active = opt;
    var img = qs(card.el, '[data-img]');
    if (img && opt.image) img.src = opt.image;
    this.updateCard(card);
  };

  P.applyFilter = function () {
    var self = this;
    var visible = 0, keys = [];
    this.cards.forEach(function (card) {
      var hideOos = self.oosBehaviour === 'hide' && card.options.every(function (o) { return o.available === false; });
      var match = self.filter === 'all' || card.range === self.filter;
      var vis = match && !hideOos;
      show(card.el, vis);
      if (vis) { visible++; keys.push(card.key); }
    });
    show(this.el.gridEmpty, visible === 0 && this.cards.length > 0);
    // P4b: a filter change re-lays the grid — any card still waiting on its scroll reveal is shown now.
    var key = keys.join(',');
    if (this.visKey && key !== this.visKey) this.revealNow();
    this.visKey = key;
  };

  /* ---------------- stock ---------------- */
  P.resolveStock = function () {
    var self = this;
    var stockEl = qs(this.root, '[data-bk-bb-stock]');
    var map = stockEl ? parseMaybeJSON(stockEl.textContent) : null;
    var pending = [];

    if (map && typeof map === 'object') {
      this.stockSource = 'collection';
      this.items.forEach(function (i) {
        var e = map[i.variantId];
        if (e && isFinite(e.p)) { i.available = !!e.a; i.pricePence = parseInt(e.p, 10); }
        else pending.push(i);   // not in the collection, or no usable live price → fall through to the per-product check (P4g)
      });
    } else {
      pending = this.items.slice();
    }

    if (!pending.length) { this.cards.forEach(function (c) { self.updateCard(c); }); this.render(); this.afterPrices(); return Promise.resolve(); }
    if (this.stockSource === 'none') this.stockSource = 'products-js';

    // Mark pending cards as "checking" so the add control is disabled until the batch lands.
    this.cards.forEach(function (c) { if (c.options.some(function (o) { return o.available === null; })) { c.checking = true; self.updateCard(c); } });

    var handles = [];
    pending.forEach(function (i) { if (handles.indexOf(i.handle) < 0) handles.push(i.handle); });

    var run = function (start) {
      if (start >= handles.length) return Promise.resolve();
      var slice = handles.slice(start, start + STOCK_BATCH);
      return Promise.all(slice.map(function (h) { return self.fetchProductAvailability(h); })).then(function () {
        self.cards.forEach(function (c) {
          if (c.options.every(function (o) { return o.available !== null; })) { c.checking = false; }
          self.updateCard(c);
        });
        self.applyFilter();
        self.render();
        self.motionRefresh();     // P4b: card heights can change once "Checking availability…" clears
        return run(start + STOCK_BATCH);
      });
    };
    return run(0).then(function () { self.afterPrices(); });
  };

  /**
   * P4g: once live prices are in — re-sort a price_asc grid (it was built before prices existed) and
   * record, report-only, every variant whose live price differs from the Appstle rule's price.
   */
  P.afterPrices = function () {
    var self = this;
    if (this.destroyed) return;
    if (this.sortMode === 'price_asc') { this.revealNow(); this.buildCards(); this.renderGrid(); this.render(); this.motionRefresh(); }   // reveal set released before the rebuild (Codex)
    var diffs = this.items.filter(function (i) { return i.pricePence != null && i.pricePence !== i.rulePricePence; }).map(function (i) {
      return { handle: i.handle, variantId: i.variantId, rulePence: i.rulePricePence, livePence: i.pricePence };
    });
    var unknown = this.items.filter(function (i) { return i.pricePence == null; }).map(function (i) { return i.handle; });
    this.priceDiffs = diffs;
    window.BKBundleBuilder.priceDiffs = diffs;
    window.BKBundleBuilder.priceUnknown = unknown;
    if (window.console && console.info) console.info('bk-bb:prices source=' + this.stockSource + ' diffs=' + diffs.length + ' unknown=' + unknown.length + ' ' + JSON.stringify(diffs));
  };

  P.fetchProductAvailability = function (handle) {
    var self = this;
    return this.fetchJSON(this.routesRoot + 'products/' + encodeURIComponent(handle) + '.js', { retries: 1 }).then(function (p) {
      (p.variants || []).forEach(function (v) {
        var it = self.byVariant[String(v.id)];
        if (it) { it.available = !!v.available; if (isFinite(v.price)) it.pricePence = parseInt(v.price, 10); }
      });
      // A pool variant missing from the product JSON no longer exists → unavailable. Never sell without a live price.
      self.items.forEach(function (it) { if (it.handle === handle && (it.available === null || it.pricePence == null)) it.available = false; });
    }).catch(function () {
      // Product not found (404) or network — treat as unavailable, never as available.
      self.items.forEach(function (it) { if (it.handle === handle && it.available === null) it.available = false; });
    });
  };

  /* ---------------- state ---------------- */
  P.storageKey = function () { return 'bk-bb:' + this.token; };

  P.hydrateState = function () {
    var self = this;
    var params = new URLSearchParams(window.location.search);
    var b = params.get(URL_PARAM);
    if (b) {
      var m = this.parseSelectionString(b);
      if (m.size) { this.sel = m; this.hydratedFromUrl = true; this.persist(); return; }
    }
    try {
      var raw = sessionStorage.getItem(this.storageKey());
      var arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) {
        arr.forEach(function (pair) { var id = String(pair[0]), q = parseInt(pair[1], 10); if (self.byVariant[id] && q > 0) self.sel.set(id, q); });
        this.capToMax();
      }
    } catch (e) { /* storage unavailable — in-memory only */ }
  };

  P.parseSelectionString = function (str) {
    var self = this;
    var map = new Map();
    String(str).split(',').forEach(function (part) {
      var bits = part.split(':');
      var id = String(bits[0] || '').trim();
      var q = parseInt(bits[1] || '1', 10);
      if (self.byVariant[id] && q > 0) map.set(id, (map.get(id) || 0) + q);
    });
    this.sel = map; this.capToMax(); map = this.sel;
    return map;
  };

  P.capToMax = function () {
    var max = this.config.max;
    var units = this.units();
    if (units <= max) return;
    // Trim from the last-added entries until within max.
    var entries = Array.from(this.sel.entries());
    for (var i = entries.length - 1; i >= 0 && units > max; i--) {
      var over = units - max;
      var q = entries[i][1];
      var take = Math.min(q, over);
      if (q - take <= 0) this.sel.delete(entries[i][0]); else this.sel.set(entries[i][0], q - take);
      units -= take;
    }
  };

  P.persist = function () {
    var arr = Array.from(this.sel.entries());
    try { sessionStorage.setItem(this.storageKey(), JSON.stringify(arr)); } catch (e) { /* ignore */ }
    try {
      var url = new URL(window.location.href);
      if (arr.length) url.searchParams.set(URL_PARAM, arr.map(function (p) { return p[0] + ':' + p[1]; }).join(','));
      else url.searchParams.delete(URL_PARAM);
      history.replaceState(history.state, '', url.toString());
    } catch (e) { /* ignore */ }
  };

  P.units = function () { var n = 0; this.sel.forEach(function (q) { n += q; }); return n; };

  P.subtotalPence = function () {
    var self = this, s = 0;
    this.sel.forEach(function (q, id) { var it = self.byVariant[id]; if (it && it.pricePence != null) s += it.pricePence * q; });
    return s;
  };

  P.tierFor = function (units) {
    var tiers = this.config.tiers, cur = null, next = null, idx = -1;
    for (var i = 0; i < tiers.length; i++) {
      if (units >= tiers[i].units) { cur = tiers[i]; idx = i; }
      else if (!next) next = tiers[i];
    }
    return { current: cur, next: next, idx: idx, isMax: !!cur && !next };
  };

  P.estimatedSavingPence = function (pct) {
    var self = this, s = 0;
    if (!pct) return 0;
    this.sel.forEach(function (q, id) { var it = self.byVariant[id]; if (it && it.pricePence != null) s += roundHalfDown(it.pricePence * q * pct / 100); });
    return s;
  };

  P.changeQty = function (variantId, delta, fromCardEl) {
    var it = this.byVariant[variantId];
    if (!it || this.inflight) return;
    var cur = this.sel.get(variantId) || 0;
    var units = this.units();
    if (delta > 0) {
      if (it.available === false || it.available === null) return;
      if (units + delta > this.config.max) { this.announce(fill(this.labels.maxReached, { max: this.config.max })); return; }
    }
    var next = clamp(cur + delta, 0, this.config.max);
    // P4b: a row leaving the summary collapses first; state changes now, the list reflows when it is done.
    if (next === 0 && cur > 0) this.collapseRow(variantId);
    if (next === 0) this.sel.delete(variantId); else this.sel.set(variantId, next);
    this.confirmedSaving = null;
    this.hideSubmitError();
    this.persist();
    this.render();
    if (delta > 0) this.pulseBadge(variantId);
    if (fromCardEl && delta > 0 && cur === 0) {
      var inc = qs(fromCardEl, '[data-inc]');
      if (inc && !inc.hidden && this.enableStepper) inc.focus();
    }
  };

  P.applyPreset = function (id) {
    var self = this;
    var preset = (this.settings.presets || []).filter(function (p) { return p.id === id; })[0];
    if (!preset) return;
    var map = new Map();
    String(preset.handles || '').split(/\r?\n|,/).map(function (h) { return h.trim(); }).filter(Boolean).forEach(function (h) {
      var it = self.byHandle[h];
      if (it && it.available !== false) map.set(it.variantId, (map.get(it.variantId) || 0) + 1);
    });
    if (!map.size) return;
    this.sel = map; this.capToMax(); this.confirmedSaving = null; this.persist(); this.render();
    if (this.el.rail) this.el.rail.scrollIntoView({ block: 'nearest' });
  };

  /* ---------------- render ---------------- */
  P.render = function () {
    var self = this;
    var units = this.units();
    var t = this.tierFor(units);
    var pct = t.current ? t.current.pct : 0;
    var subtotal = this.subtotalPence();
    var saving = this.confirmedSaving != null ? this.confirmedSaving : this.estimatedSavingPence(pct);
    var total = Math.max(0, subtotal - saving);
    var min = this.config.min;

    var tierChanged = t.idx !== this.lastTierIdx;
    var wentUp = t.idx > this.lastTierIdx;

    // Cards
    this.cards.forEach(function (c) { self.updateCard(c, pct, units); });

    // Ladder (must be current BEFORE the unlock flash targets .is-current)
    this.renderLadder(units, t);

    // Tier change → unlock state + announcement
    if (tierChanged) {
      if (this.rendered) {
        if (t.current && wentUp) {
          this.flashUnlock();
          var msg = fill(this.labels.announceUnlock, { pct: pct });
          if (t.next) msg += ' ' + this.nextPrompt(units, t);
          this.announce(msg);
        } else if (t.current) {
          this.announce(fill(this.labels.announceTier, { pct: pct }));
        } else {
          this.announce(this.labels.announceNone);
        }
      }
      this.lastTierIdx = t.idx;
    }
    this.rendered = true;

    // Prompt line
    var prompt = this.nextPrompt(units, t);
    text(this.el.tierMsg, prompt);
    if (this.el.tierMsg) this.el.tierMsg.classList.toggle('is-max', t.isMax);

    // Suggestions
    this.renderSuggestions(units, t);

    // Items
    this.renderItems();
    show(this.el.empty, this.sel.size === 0);
    show(this.el.clear, this.sel.size > 0);
    text(this.el.railCount, units ? units + ' ' + (units === 1 ? this.labels.itemWord : this.labels.itemsWord) : '');

    // Totals (P4b: count to the new value when motion is on; written instantly otherwise)
    this.setMoney(this.el.subtotal, subtotal, false);
    this.setMoney(this.el.saving, saving, true);
    this.setMoney(this.el.total, total, false);
    text(this.el.savingLabel, this.confirmedSaving != null ? this.labels.confirmedLabel : this.labels.estimateLabel);
    show(this.el.estimateNote, this.confirmedSaving == null && saving > 0);

    // CTA
    var canCheckout = units >= min && !this.inflight;
    if (this.el.checkout) { this.el.checkout.disabled = !canCheckout; }
    var reason = '';
    if (units === 0) reason = fill(this.labels.ctaReasonEmpty, { min: min });
    else if (units < min && this.resumedFromCart) reason = fill(this.labels.errorBelowMin, { min: min }); // edited a resumed bundle below the minimum — say so plainly
    else if (units < min) reason = pluralise(fill(this.labels.ctaReasonMin, { n: min - units }), min - units, this.labels.itemWord, this.labels.itemsWord);
    text(this.el.ctaReason, reason);

    // Mobile bar + sheet handle
    this.setPct(this.el.barPct, pct);
    // Mobile bar uses the shorter mobile labels (P4a.2); desktop rail copy is untouched.
    var barMsg = units === 0
      ? fill(this.labels.mobileBarEmpty, { min: min, pct: this.config.tiers[0] ? this.config.tiers[0].pct : 0 })
      : this.nextPrompt(units, t, true);
    text(this.el.barMsg, barMsg);
    this.setProgress(this.progressPercent(units));
    if (this.el.barStops && !this.el.barStops.childElementCount) {
      this.config.tiers.forEach(function (tier) { var s = document.createElement('span'); text(s, tier.units + (tier === self.config.tiers[self.config.tiers.length - 1] ? '+' : '')); self.el.barStops.appendChild(s); });
    }
    var handleText = units === 0
      ? barMsg
      : fill(this.labels.mobileBar, { n: units, total: this.fmt.format(total / 100), saving: this.fmt.format(saving / 100) });
    if (units === 1) handleText = handleText.replace(new RegExp('\\b' + this.labels.itemsWord + '\\b'), this.labels.itemWord);
    text(this.el.sheetSummary, handleText);
  };

  /** Progress along the ladder: 0 at 0 units → 100 at top tier. */
  P.progressPercent = function (units) {
    var tiers = this.config.tiers;
    if (!tiers.length) return 0;
    var top = tiers[tiers.length - 1].units;
    return Math.round(clamp(units / top, 0, 1) * 100);
  };

  /** mobile=true swaps in the shorter bar labels where a mobile variant exists. */
  P.nextPrompt = function (units, t, mobile) {
    var L = this.labels;
    if (t.isMax) return (mobile && L.tierMaxMobile) || L.tierMax;
    if (!t.next) return '';
    var n = t.next.units - units;
    var tpl = t.current ? L.nextTier : ((mobile && L.minNotMetMobile) || L.minNotMet);
    return pluralise(fill(tpl, { n: n, pct: t.next.pct }), n, L.itemWord, L.itemsWord);
  };

  P.flashUnlock = function () {
    var self = this;
    if (this.root.classList.contains('bk-bb--unlock-off')) return;
    var current = qsa(this.root, '[data-tier].is-current');
    // P4b: the accent belongs to the newly current row only — a previous row still inside its
    // 1.4s window (rapid adds) hands it over rather than both showing orange.
    qsa(this.root, '[data-tier].is-unlocking').forEach(function (el) { if (current.indexOf(el) < 0) el.classList.remove('is-unlocking'); });
    var targets = qsa(this.root, '[data-pct]').concat(current);
    targets.forEach(function (el) { el.classList.add('is-unlocking'); });
    clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(function () { qsa(self.root, '.is-unlocking').forEach(function (el) { el.classList.remove('is-unlocking'); }); }, UNLOCK_MS);
    // P4b: the newly current ladder row settles 1 → 1.04 → 1 while the 1.4s accent state runs. Nothing else moves.
    if (this.motionOn() && current.length) {
      var g = window.gsap, half = MOTION.unlockScale / 2;
      g.killTweensOf(current);
      this.motion.track(g.timeline()
        .to(current, { scale: 1.04, duration: half, ease: 'power2.out' })
        .to(current, { scale: 1, duration: half, ease: 'power2.out', clearProps: 'transform' }));
    }
  };

  /** P4g: "Save 5% · 2 items | Save 10% · 3 items | …" from the rule; the top tier renders "{n}+". */
  P.renderTierStrip = function () {
    var self = this;
    var list = this.el.tierStripList;
    if (!list || !this.config || !this.config.tiers.length) return;
    list.innerHTML = '';
    this.config.tiers.forEach(function (tier, i) {
      var last = i === self.config.tiers.length - 1;
      var li = document.createElement('li');
      li.className = 'bk-bb__tierstrip-item';
      if (i > 0) { var sep = document.createElement('span'); sep.className = 'bk-bb__tierstrip-sep'; sep.setAttribute('aria-hidden', 'true'); text(sep, '|'); li.appendChild(sep); }
      li.appendChild(document.createTextNode(fill(self.labels.tierStripItem, { n: last ? tier.units + '+' : tier.units, pct: tier.pct })));
      list.appendChild(li);
    });
    show(this.el.tierStrip, true);
  };

  P.renderLadderSkeleton = function () {
    var self = this;
    if (!this.el.ladder) return;
    this.el.ladder.innerHTML = '';
    this.config.tiers.forEach(function (tier, i) {
      var row = self.clone('ladder-row');
      if (!row) return;
      var last = i === self.config.tiers.length - 1;
      row.setAttribute('data-units', tier.units);
      text(qs(row, '[data-tier-label]'), fill(self.labels.ladderRow, { n: last ? tier.units + '+' : tier.units, pct: tier.pct }));
      text(qs(row, '[data-tier-pct]'), tier.pct + '%');
      self.el.ladder.appendChild(row);
    });
  };

  P.renderLadder = function (units, t) {
    var self = this;
    qsa(this.el.ladder || this.root, '[data-tier]').forEach(function (row, i) {
      var tier = self.config.tiers[i];
      var achieved = units >= tier.units;
      var isCurrent = t.current === tier;
      var isNext = t.next === tier;
      row.classList.toggle('is-achieved', achieved && !isCurrent);
      row.classList.toggle('is-current', isCurrent);
      row.classList.toggle('is-next', isNext);
      var word = qs(row, '[data-tier-unlocked]');   // P4e: "Unlocked" after the current row's label only
      if (word) { text(word, isCurrent ? self.labels.tierUnlocked : ''); show(word, isCurrent); }
      row.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      show(row, self.showLadderFull || achieved || isNext);
    });
  };

  P.updateCard = function (card, pct, units) {
    var self = this;
    if (!card.el) return;
    if (pct === undefined) { var t = this.tierFor(this.units()); pct = t.current ? t.current.pct : 0; units = this.units(); }
    var item = card.active;
    var qty = this.sel.get(item.variantId) || 0;
    var cardQty = card.options.reduce(function (n, o) { return n + (self.sel.get(o.variantId) || 0); }, 0);
    var unavailable = item.available === false;
    var checking = item.available === null;
    var atMax = units >= this.config.max;

    card.el.classList.toggle('is-selected', cardQty > 0);
    card.el.classList.toggle('is-unavailable', unavailable);
    card.el.classList.toggle('is-checking', checking);

    // Size buttons
    qsa(card.el, '[data-size]').forEach(function (b) {
      var opt = self.byVariant[b.getAttribute('data-variant')];
      b.setAttribute('aria-pressed', opt === item ? 'true' : 'false');
      b.disabled = opt.available === false;
      var q = self.sel.get(opt.variantId) || 0;
      text(b, sizeLabel(opt) + (q ? ' · ' + q : ''));
    });

    // Prices
    var base = qs(card.el, '[data-price-base]');
    var eff = qs(card.el, '[data-price-eff]');
    text(base, item.pricePence == null ? '' : this.fmt.format(item.pricePence / 100));
    var showEff = this.showEffective && pct > 0 && !unavailable && item.pricePence != null;
    if (base) base.classList.toggle('is-struck', showEff);
    if (eff) {
      show(eff, showEff);
      if (showEff) text(eff, fill(this.labels.effectivePrice, { price: this.fmt.format(roundHalfDown(item.pricePence * (100 - pct) / 100) / 100), pct: pct }));
    }

    // Status / badge
    var badge = qs(card.el, '[data-badge]');
    var status = qs(card.el, '[data-status]');
    if (unavailable) { text(badge, this.labels.oos); badge.className = 'bk-bb__card-badge is-oos'; show(badge, true); }
    else if (cardQty > 0) { text(badge, cardQty + ' ' + (cardQty === 1 ? this.labels.itemWord : this.labels.itemsWord)); badge.className = 'bk-bb__card-badge is-qty'; show(badge, true); }
    else show(badge, false);
    if (checking) { text(status, this.labels.checking); show(status, true); }
    else if (!unavailable && atMax && qty === 0) { text(status, this.labels.maxReached); show(status, true); }
    else show(status, false);

    // Controls
    var add = qs(card.el, '[data-add]');
    var stepper = qs(card.el, '[data-stepper]');
    var inc = qs(card.el, '[data-inc]');
    var dec = qs(card.el, '[data-dec]');
    var useStepper = this.enableStepper && qty > 0;
    var locked = !!this.inflight; // submit in flight — nothing may change the payload
    show(add, !useStepper);
    show(stepper, useStepper);
    if (add) { add.disabled = locked || unavailable || checking || atMax; add.setAttribute('aria-label', this.labels.add + ': ' + card.title + (card.grouped ? ' ' + sizeLabel(item) : '')); if (atMax && !unavailable) add.title = this.labels.maxReached; else add.removeAttribute('title'); }
    if (inc) { inc.disabled = locked || atMax || unavailable || checking; if (atMax) inc.title = this.labels.maxReached; else inc.removeAttribute('title'); }
    if (dec) dec.disabled = locked || qty === 0;
    qsa(card.el, '[data-size]').forEach(function (b) { if (locked) b.disabled = true; });
    text(qs(card.el, '[data-qty]'), qty);
  };

  P.renderItems = function () {
    var self = this;
    var list = this.el.items;
    if (!list) return;
    if (this.collapsing) return;   // P4b: a row is collapsing — the list is rebuilt when it finishes
    var had = {};
    var old = qsa(list, '[data-item]');
    old.forEach(function (r) { had[r.getAttribute('data-variant')] = true; });
    if (old.length && window.gsap) window.gsap.killTweensOf(old);   // nothing keeps tweening a detached row
    var fresh = [];
    list.innerHTML = '';
    this.sel.forEach(function (q, id) {
      var it = self.byVariant[id];
      if (!it) return;
      var row = self.clone('summary-row');
      if (!row) return;
      row.setAttribute('data-variant', id);
      if (self.rendered && !had[id]) fresh.push(row);
      var img = qs(row, '[data-img]'); if (img) img.src = it.image;
      text(qs(row, '[data-title]'), it.title);
      text(qs(row, '[data-price]'), it.pricePence == null ? '' : self.fmt.format(it.pricePence * q / 100));
      text(qs(row, '[data-qty]'), q);
      var inc = qs(row, '[data-inc]'); var dec = qs(row, '[data-dec]'); var rm = qs(row, '[data-remove]');
      var locked = !!self.inflight;
      if (inc) { inc.disabled = locked || self.units() >= self.config.max; on(inc, 'click', function () { self.changeQty(id, 1); }); }
      if (dec) { dec.disabled = locked; on(dec, 'click', function () { self.changeQty(id, -1); }); }
      if (rm) { rm.disabled = locked; on(rm, 'click', function () { self.changeQty(id, -q); }); }
      list.appendChild(row);
    });
    // P4b: only rows that were not in the list before slide in (y:8 → 0, fade). Quantity changes do not move.
    if (fresh.length && this.motionOn()) {
      this.motion.track(window.gsap.from(fresh, { opacity: 0, y: 8, duration: MOTION.rowIn, ease: 'power2.out', clearProps: 'opacity,transform' }));
    }
  };

  /** Adjacency prompt: 1 unit short of the next tier → 2–3 unselected, available pool items from the same range(s). */
  P.renderSuggestions = function (units, t) {
    var self = this;
    if (!this.showSuggestions) return;          // P4f: switched off — the block is not even rendered
    var box = this.el.suggest;
    if (!box) return;
    var oneShort = this.enableSuggestions && t.next && (t.next.units - units === 1) && units > 0;
    if (!oneShort) { show(box, false); return; }

    var rangeCount = {};
    this.sel.forEach(function (q, id) { var it = self.byVariant[id]; if (it) rangeCount[it.range] = (rangeCount[it.range] || 0) + q; });
    var ranges = Object.keys(rangeCount).sort(function (a, b) { return rangeCount[b] - rangeCount[a]; });

    var pool = this.items.filter(function (i) { return !self.sel.has(i.variantId) && i.available !== false && i.available !== null; });
    var picks = [];
    ranges.forEach(function (r) { pool.filter(function (i) { return i.range === r; }).sort(function (a, b) { return a.pricePence - b.pricePence; }).forEach(function (i) { if (picks.length < 3 && picks.indexOf(i) < 0) picks.push(i); }); });
    if (picks.length < 2) pool.slice().sort(function (a, b) { return a.pricePence - b.pricePence; }).forEach(function (i) { if (picks.length < 3 && picks.indexOf(i) < 0) picks.push(i); });
    if (!picks.length) { show(box, false); return; }

    text(this.el.suggestLabel, fill(this.labels.gapSuggestion, { pct: t.next.pct }));
    this.el.suggestList.innerHTML = '';
    picks.forEach(function (it) {
      var row = self.clone('suggestion');
      if (!row) return;
      var img = qs(row, '[data-img]'); if (img) img.src = it.image;
      text(qs(row, '[data-title]'), it.title);
      text(qs(row, '[data-price]'), it.pricePence == null ? '' : self.fmt.format(it.pricePence / 100));
      var add = qs(row, '[data-add]');
      if (add) { add.setAttribute('aria-label', self.labels.suggestAdd + ': ' + it.title); on(add, 'click', function () { self.changeQty(it.variantId, 1); }); }
      self.el.suggestList.appendChild(row);
    });
    show(box, true);
  };

  /* ---------------- bottom sheet ---------------- */
  P.toggleSheet = function (force) {
    var rail = this.el.rail;
    if (!rail) return;
    var open = typeof force === 'boolean' ? force : !rail.classList.contains('is-open');
    rail.classList.toggle('is-open', open);
    if (this.el.sheetToggle) this.el.sheetToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    show(this.el.scrim, open);
    if (open) {
      // Focus the scrolling body (P4a.2) so keyboard users can scroll the ladder/items; the footer holds the checkout.
      var first = qs(rail, '[data-rail-body]') || qs(rail, '.bk-bb__rail-inner');
      if (first) { if (!first.hasAttribute('tabindex')) first.setAttribute('tabindex', '-1'); first.focus({ preventScroll: true }); }
    }
    else if (this.el.sheetToggle) this.el.sheetToggle.focus();
  };

  /* ---------------- cart: read, resume, start over ---------------- */
  P.getCart = function () { return this.fetchJSON(this.routesRoot + 'cart.js', { retries: 1 }); };

  /** Only lines carrying OUR token. Rebuy's free-gift line and everything else is ignored. Cart attributes are never read. */
  P.taggedLines = function (cart) {
    var token = this.token;
    return (cart && cart.items ? cart.items : []).filter(function (l) { return l.properties && l.properties[APPSTLE_KEYS.id] === token; });
  };

  /** Discounted subtotal of OUR lines only (final_line_price already carries the Function's allocation). Minor units. */
  P.discountedSubtotalPence = function (cart) {
    return this.taggedLines(cart).reduce(function (n, l) { return n + (parseInt(l.final_line_price, 10) || 0); }, 0);
  };

  P.checkCartForResume = function () {
    var self = this;
    return this.getCart().then(function (cart) {
      var lines = self.taggedLines(cart);
      self.existingLines = lines;
      if (!lines.length) return;
      show(self.el.resume, true);
      // P4c.1: move focus to the prompt's group (announces title + text), not the CTA — a script focus
      // with no prior pointer interaction counts as :focus-visible and drew a ring on "Resume your bundle".
      var box = qs(self.el.resume, '.bk-bb__resume-box');
      if (box) { if (!box.hasAttribute('tabindex')) box.setAttribute('tabindex', '-1'); box.focus({ preventScroll: true }); }
    }).catch(function () { /* cart unreadable — builder still works; submit re-reads */ });
  };

  P.resume = function () {
    var self = this;
    var map = new Map();
    (this.existingLines || []).forEach(function (l) {
      var id = String(l.variant_id);
      if (self.byVariant[id]) map.set(id, (map.get(id) || 0) + l.quantity);
    });
    if (map.size) { this.sel = map; this.capToMax(); this.confirmedSaving = null; this.resumedFromCart = true; this.persist(); this.render(); }
    show(this.el.resume, false);
    this.announce(this.labels.resumeDone);
    if (this.el.rail) this.el.rail.scrollIntoView({ block: 'nearest' });
  };

  P.startOver = function () {
    var self = this;
    var btn = this.el.resumeNo; if (btn) btn.disabled = true;
    return this.removeTaggedLines().then(function () {
      self.existingLines = [];
      if (!self.hydratedFromUrl) { self.sel.clear(); self.persist(); }
      self.confirmedSaving = null;
      self.render();
      show(self.el.resume, false);
      self.announce(self.labels.startOverDone);
    }).catch(function () {
      if (btn) btn.disabled = false;
    });
  };

  /** Removes ONLY tagged lines, by line key, one change.js call each (D-05: quantity 0 removes). */
  P.removeTaggedLines = function () {
    var self = this;
    return this.getCart().then(function (cart) {
      var lines = self.taggedLines(cart);
      var chain = Promise.resolve();
      lines.forEach(function (l) {
        chain = chain.then(function () {
          return self.fetchJSON(self.routesRoot + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: l.key, quantity: 0 })
          });
        });
      });
      return chain;
    });
  };

  /* ---------------- submit: replace → add → verify → hand off to the cart drawer (P4d) ---------------- */
  P.setBusy = function (busy) {
    if (this.el.checkout) { this.el.checkout.disabled = busy || this.units() < this.config.min; this.el.checkout.classList.toggle('is-busy', busy); this.el.checkout.setAttribute('aria-busy', busy ? 'true' : 'false'); }
    if (this.el.checkoutLabel) text(this.el.checkoutLabel, busy ? (this.labels.ctaBusy || this.ctaIdleLabel) : this.ctaIdleLabel);   // P4d: "Adding…" while in flight
    this.pulseBusy(busy);
    qsa(this.root, '[data-add],[data-inc],[data-dec],[data-remove],[data-clear],[data-preset]').forEach(function (b) { if (busy) b.setAttribute('data-was-disabled', b.disabled ? '1' : '0'); b.disabled = busy ? true : b.getAttribute('data-was-disabled') === '1'; });
  };

  P.showSubmitError = function (msg) {
    text(this.el.submitErrorText, msg);
    show(this.el.submitError, true);
    if (this.el.submitError) this.el.submitError.focus();
    this.announce(msg);
  };
  P.hideSubmitError = function () { show(this.el.submitError, false); };

  P.linesFromState = function () {
    var self = this, out = [];
    this.sel.forEach(function (q, id) { var it = self.byVariant[id]; if (it && q > 0) out.push({ variantId: id, qty: q, item: it }); });
    return out;
  };

  P.submit = function () {
    var self = this;
    if (this.inflight) return;
    var lines = this.linesFromState();
    var units = lines.reduce(function (n, l) { return n + l.qty; }, 0);
    if (units < this.config.min) return;

    var payloadHash = hashOf(JSON.stringify(lines.map(function (l) { return [l.variantId, l.qty]; })));
    this.inflight = payloadHash;
    this.rebuyShownDuringSubmit = false;
    this.hideSubmitError();
    this.setBusy(true);

    this.recheckStock(lines)
      .then(function (result) {
        lines = result.lines;
        if (result.dropped.length) {
          result.dropped.forEach(function (d) { self.sel.delete(d.variantId); });
          self.persist();
          self.render();
          var titles = result.dropped.map(function (d) { return d.item.title; }).join(', ');
          var unitsNow = lines.reduce(function (n, l) { return n + l.qty; }, 0);
          if (unitsNow < self.config.min) {
            throw new UserFacing(fill(self.labels.errorOosDropped, { title: titles }) + ' ' + fill(self.labels.errorBelowMin, { min: self.config.min }));
          }
          self.announce(fill(self.labels.errorOosDropped, { title: titles }));
        }
        // Replace, never accumulate: drop any previously tagged lines first.
        return self.removeTaggedLines();
      })
      .then(function () { return self.addLines(lines); })
      .then(function () { return self.verifyDiscount(lines); })
      .then(function (v) {
        if (self.destroyed) return 'cancelled';           // section unloaded mid-submit: no render, no hand-off
        if (!v.ok) throw new UserFacing(self.labels.errorNoDiscount, v);
        self.confirmedSaving = v.savingPence;
        self.render();
        return self.handOffToCart();
      })
      .then(function (mode) {
        if (self.destroyed || mode === 'cancelled') return;
        self.handoff = mode;
        if (mode === 'navigate') {
          // Restore the idle state first (Codex P4d #5): a bfcache restore must not come back mid-"Adding…".
          self.inflight = null; self.setBusy(false); self.render();
          window.location.assign(self.cartUrl);
          return;
        }
        self.announce(self.labels.successAdded);
        self.probeGiftLine();                       // report-only (P4d item 3), after the hand-off, never blocks it
      })
      .catch(function (err) {
        if (self.destroyed) return;
        var msg = err instanceof UserFacing ? err.message : self.labels.errorAdd;
        if (!(err instanceof UserFacing) && err && err.detail) msg += ' (' + err.detail + ')';
        self.showSubmitError(msg);
      })
      .then(function () {
        if (self.destroyed) return;
        self.inflight = null; self.setBusy(false); self.render();
      });
  };

  /**
   * P4d: hand the verified bundle to the cart UI and stop. Resolves to
   *   'already-open'  — Rebuy opened its drawer itself during our add (show event since submit, or .visible): do nothing;
   *   'rebuy-opened'  — we called the documented window.Rebuy.SmartCart.show() (D-14);
   *   'navigate'      — no Smart Cart API (or not ready within REBUY_READY_WAIT_MS): caller goes to routes.cart_url.
   * Never /checkout.
   */
  P.handOffToCart = function () {
    var self = this;
    if (this.destroyed) return Promise.resolve('cancelled');
    var sc = window.Rebuy && window.Rebuy.SmartCart;
    if (!sc || typeof sc.show !== 'function') return Promise.resolve('navigate');
    var openedItself = function () { return sc.visible === true || self.rebuyShownDuringSubmit; };
    if (openedItself()) return Promise.resolve('already-open');

    // Not ready yet: wait for the documented ready event, a show event (Rebuy opened it meanwhile), the
    // timeout, or destroy(). Resolves 'ready' | 'shown' | 'timeout' | 'cancelled'.
    var ready = sc.status === 'ready' ? Promise.resolve('ready') : new Promise(function (resolve) {
      var done = false, timer = null;
      var finish = function (how) {
        if (done) return; done = true;
        clearTimeout(timer);
        document.removeEventListener('rebuy:smartcart.ready', onReady);
        document.removeEventListener('rebuy:smartcart.show', onShow);
        self.cancelHandoff = null;
        resolve(how);
      };
      var onReady = function () { finish('ready'); };
      var onShow = function () { finish('shown'); };
      document.addEventListener('rebuy:smartcart.ready', onReady);
      document.addEventListener('rebuy:smartcart.show', onShow);
      timer = setTimeout(function () { finish(sc.status === 'ready' ? 'ready' : 'timeout'); }, REBUY_READY_WAIT_MS);
      self.cancelHandoff = function () { finish('cancelled'); };
    });

    return ready.then(function (how) {
      if (how === 'cancelled' || self.destroyed) return 'cancelled';
      if (how === 'shown' || openedItself()) return 'already-open';   // checked BEFORE the timeout fallback (Codex P4d #3)
      if (how !== 'ready') return 'navigate';
      try { sc.show(); } catch (e) { return 'navigate'; }
      return 'rebuy-opened';
    });
  };

  /**
   * P4d item 3 — REPORT-ONLY probe, no UI. After the hand-off, poll /cart.js for up to ~3s for
   * Rebuy's free-gift line (line property _source === "Rebuy") when the discounted subtotal of
   * our lines is at or above the gift threshold Rebuy reads (threshold read from Smart Cart settings
   * when exposed; null = unknown → poll anyway and report). Result → console "bk-bb:gift" and
   * window.BKBundleBuilder.lastGiftProbe.
   */
  P.probeGiftLine = function () {
    var self = this;
    var t0 = Date.now();
    var threshold = this.rebuyGiftThresholdPence();
    var giftIds = this.rebuyGiftProductIds || [];
    var result = { thresholdPence: threshold, giftProductIds: giftIds, bundleSubtotalPence: null, cartSubtotalPence: null, expectGift: null, polls: 0, ms: 0, timedOut: false, giftLine: null };
    // "Rebuy line" per the brief = _source: Rebuy. A gift additionally costs £0 and, when Rebuy exposes the tier's
    // product ids, is one of them — _source alone is provenance (a paid Rebuy upsell carries it too; Codex r2).
    var isRebuy = function (l) { return !!(l.properties && l.properties._source === 'Rebuy'); };
    var isGift = function (l) { return isRebuy(l) && (parseInt(l.final_line_price, 10) || 0) === 0 && (!giftIds.length || giftIds.indexOf(String(l.product_id)) >= 0); };
    var expired = function () { return Date.now() - t0 >= GIFT_DEADLINE_MS; };
    var done = false;
    var finish = function (r) {
      if (done) return r; done = true;
      r.ms = Date.now() - t0;
      window.BKBundleBuilder.lastGiftProbe = r;
      if (window.console && console.info) console.info('bk-bb:gift ' + JSON.stringify(r));
      return r;
    };
    var attempt = function () {
      if (self.destroyed) return result;
      if (expired()) { result.timedOut = true; return result; }                  // hard deadline checked BEFORE every fetch (Codex r2 #1)
      return self.getCart().then(function (cart) {
        if (done) return result;                                                 // a late response after the deadline changes nothing
        result.polls += 1;
        result.bundleSubtotalPence = self.discountedSubtotalPence(cart);
        // Rebuy's bar reads the whole cart (discounts included, exclude_discount_in_subtotal=false live) minus its own £0 gift line.
        result.cartSubtotalPence = (parseInt(cart.items_subtotal_price, 10) || 0) - (cart.items || []).filter(isGift).reduce(function (n, l) { return n + (parseInt(l.final_line_price, 10) || 0); }, 0);
        result.expectGift = threshold == null ? null : result.cartSubtotalPence >= threshold;
        var rebuyLines = (cart.items || []).filter(isRebuy);
        var gift = rebuyLines.filter(isGift)[0];
        result.rebuyLines = rebuyLines.length;
        if (gift) {
          result.giftLine = { key: gift.key, handle: gift.handle, product_id: String(gift.product_id), title: gift.title, quantity: gift.quantity, final_line_price: gift.final_line_price, tier: gift.properties._tier || null, attribution: gift.properties._attribution || null };
          return result;
        }
        if (result.expectGift === false) return result;                        // below the threshold: nothing to wait for
        var left = GIFT_DEADLINE_MS - (Date.now() - t0);
        if (left <= 0) { result.timedOut = true; return result; }
        return sleep(Math.min(GIFT_POLL_MS, left)).then(attempt);
      });
    };
    // Independent deadline: whatever a hanging /cart.js does, the report lands within GIFT_DEADLINE_MS (+ one tick).
    var deadline = sleep(GIFT_DEADLINE_MS + 50).then(function () { if (!done) { result.timedOut = true; } return result; });
    return Promise.race([attempt(), deadline]).catch(function (e) { result.error = String((e && e.message) || e); return result; }).then(finish);
  };

  /** Gift threshold in pence from Rebuy Smart Cart settings, or null when Rebuy does not expose one. */
  P.rebuyGiftThresholdPence = function () {
    var sc = window.Rebuy && window.Rebuy.SmartCart;
    var s = sc && sc.settings;
    if (!s) return null;
    // Live shape (15 Sep 2026, recorded in the P4d report): settings.components[] → the Tiered Progress Bar
    // component carries settings.bars[].tiers[] with { type: 'product', minimum: <major units>, products: [...] }.
    // The gift threshold is the lowest product-tier minimum across the bars. Anything else → null (unknown).
    var min = null, ids = [];
    (s.components || []).forEach(function (c) {
      if (!c || c.type !== 'tiered_progress_bar') return;
      var bars = c.settings && c.settings.bars;
      if (!Array.isArray(bars)) return;
      bars.forEach(function (b) {
        (b && Array.isArray(b.tiers) ? b.tiers : []).forEach(function (t) {
          if (!t || t.type !== 'product') return;
          var m = parseFloat(t.minimum);
          if (isFinite(m) && (min == null || m < min)) min = m;
          (Array.isArray(t.products) ? t.products : []).forEach(function (p) {
            var id = p && (p.product_id || p.id);
            if (id != null && ids.indexOf(String(id)) < 0) ids.push(String(id));
          });
        });
      });
    });
    this.rebuyGiftProductIds = ids;
    if (min != null) return Math.round(min * 100);
    return null;
  };

  function UserFacing(message, data) { this.message = message; this.data = data; }
  UserFacing.prototype = Object.create(Error.prototype);

  /** Re-check only the selected handles. Returns { lines, dropped }. */
  P.recheckStock = function (lines) {
    var self = this;
    var handles = [];
    lines.forEach(function (l) { if (handles.indexOf(l.item.handle) < 0) handles.push(l.item.handle); });
    var run = function (start) {
      if (start >= handles.length) return Promise.resolve();
      return Promise.all(handles.slice(start, start + STOCK_BATCH).map(function (h) { return self.fetchProductAvailability(h); })).then(function () { return run(start + STOCK_BATCH); });
    };
    return run(0).then(function () {
      var keep = [], dropped = [];
      lines.forEach(function (l) { if (l.item.available === false) dropped.push(l); else keep.push(l); });
      self.cards.forEach(function (c) { self.updateCard(c); });
      return { lines: keep, dropped: dropped };
    });
  };

  P.addLines = function (lines) {
    var self = this;
    var props = {};
    props[APPSTLE_KEYS.id] = this.token;
    props[APPSTLE_KEYS.type] = this.bundleType;
    props[APPSTLE_KEYS.name] = this.bundleName;
    // Assert the frozen keys before the wire — a typo here is a silent zero discount.
    if (Object.keys(props).join('|') !== '_appstle-bb-id|_appstle_bundles_type|__appstle-bb-name') throw new Error('APPSTLE_KEYS integrity check failed');

    var body = { items: lines.map(function (l) { return { id: Number(l.variantId), quantity: l.qty, properties: props }; }) };
    return fetchWithTimeout(this.routesRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }, FETCH_TIMEOUT_MS).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) { var e = new Error('add failed'); e.detail = json.description || json.message || ('HTTP ' + res.status); throw e; }
        return json;
      });
    });
  };

  /**
   * Poll /cart.js with short backoff (cap < 3s) until every expected line is present
   * AND a tagged line carries the bundle discount allocation. Reads line-level
   * allocations only — cart_level_discount_applications is always empty here.
   */
  P.verifyDiscount = function (lines) {
    var self = this;
    var expected = {};
    lines.forEach(function (l) { expected[l.variantId] = (expected[l.variantId] || 0) + l.qty; });
    var expectedUnits = lines.reduce(function (n, l) { return n + l.qty; }, 0);
    var wantDiscount = expectedUnits >= this.config.min;

    var attempt = function (i) {
      return sleep(VERIFY_DELAYS_MS[i]).then(function () { return self.getCart(); }).then(function (cart) {
        var tagged = self.taggedLines(cart);
        var got = {};
        tagged.forEach(function (l) { got[String(l.variant_id)] = (got[String(l.variant_id)] || 0) + l.quantity; });
        var allPresent = Object.keys(expected).every(function (id) { return (got[id] || 0) >= expected[id]; });

        var allocations = [];
        tagged.forEach(function (l) { (l.line_level_discount_allocations || []).forEach(function (a) { allocations.push(a); }); });
        // Only the bundle's own allocation counts. Rebuy's "Free Gift 100%" or any other
        // line-level discount must never make verification pass (Codex finding #1).
        var titled = allocations.filter(function (a) { return a.discount_application && a.discount_application.title === self.config.discountName; });
        var savingPence = titled.reduce(function (n, a) { return n + (parseInt(a.amount, 10) || 0); }, 0);

        var ok = allPresent && (!wantDiscount || titled.length > 0);
        if (ok) return { ok: true, savingPence: savingPence, cart: cart, attempts: i + 1 };
        if (i + 1 < VERIFY_DELAYS_MS.length) return attempt(i + 1);
        return { ok: false, allPresent: allPresent, allocations: allocations.length, titled: titled.length, cart: cart, attempts: i + 1 };
      });
    };
    return attempt(0);
  };

  /* ------------------------------------------------------------------ */
  /* P4b motion — GSAP + ScrollTrigger, enhancement only                  */
  /*                                                                      */
  /* Two lifetimes:                                                       */
  /*   • long-lived (reveal set + ScrollTriggers) are created inside the  */
  /*     matchMedia context and reverted by it (D-08, D-12);              */
  /*   • transient tweens (counts, pulses, rows, FAQ) are created outside */
  /*     the context so a busy session never accumulates them; they are  */
  /*     tracked in motion.live while active and killed on cleanup.       */
  /* Every helper is a no-op unless motionOn() — the instant path is the  */
  /* same code that ran in P4a.                                           */
  /* ------------------------------------------------------------------ */
  P.motionOn = function () { return !!(this.motion && this.motion.active); };

  P.initMotion = function () {
    var self = this, g = window.gsap;
    if (this.motion || this.destroyed || !g || !this.root.classList.contains('bk-bb--gsap')) return;
    if (typeof performance !== 'undefined' && performance.mark) performance.mark('bk-bb:motion:start');
    var ST = window.ScrollTrigger;
    if (ST) g.registerPlugin(ST);

    var m = this.motion = {
      active: false,
      ctx: null,
      mm: g.matchMedia(),
      snap1: g.utils.snap(1),                    // D-09
      pending: [],                               // reveal targets not yet shown
      batch: null,                               // ScrollTriggers from ScrollTrigger.batch (D-07)
      live: [],
      refreshTimer: null,
      /**
       * Keep a bounded list of scheduled transient tweens/timelines so cleanup can revert them.
       * Pruning is by `parent`: a finished or killed animation is removed from the global timeline,
       * a just-created one is not yet initialised (isActive() would wrongly drop it — Codex P4b #1).
       */
      track: function (t) {
        m.live = m.live.filter(function (x) { return !!x.parent; });
        m.live.push(t);
        return t;
      },
      /** D-11: revert() kills AND strips the inline styles an animation added — works for timelines too. */
      killLive: function () {
        m.live.forEach(function (t) { t.revert(); });
        m.live = [];
        // revert() restores each animation's STARTING inline style, which for a re-triggered pulse can be a
        // mid-scale value — so every motion-owned element is stripped explicitly (Codex P4b #4). Cards are
        // left alone: their reveal set lives in the matchMedia context and is reverted by it.
        g.set(qsa(self.root, '[data-badge], [data-tier], [data-fill], [data-checkout], [data-item]'), { clearProps: 'transform,opacity' });
        g.set(qsa(self.root, '.bk-bb__faq-a'), { clearProps: 'height,paddingBottom,opacity,overflow' });
        [self.el.subtotal, self.el.saving, self.el.total, self.el.barPct].forEach(function (el) { if (el) el.__mp = null; });
        if (self.el.barFill) self.el.barFill.__tw = false;
      }
    };

    // D-08: the whole motion layer lives inside this media query. When it stops matching
    // (user turns reduced motion on) the context is reverted and every state is applied instantly.
    m.mm.add('(prefers-reduced-motion: no-preference)', function (ctx) {
      m.ctx = ctx;
      m.active = true;
      self.root.classList.add('bk-bb--motion');   // switches off the CSS transitions GSAP replaces
      self.setupReveals();
      return function () {
        m.active = false;
        m.ctx = null;
        m.pending = [];
        m.batch = null;
        clearTimeout(m.refreshTimer);
        self.root.classList.remove('bk-bb--motion');
        m.killLive();
        self.finishCollapse();
        // An interrupted FAQ close lands closed (its aria-expanded is already "false"); an interrupted open stays open.
        qsa(self.root, '.bk-bb__faq-item').forEach(function (d) { if (d.classList.contains('is-closing')) { d.open = false; d.classList.remove('is-closing'); } d.__anim = null; });
        if (self.rendered && !self.destroyed) self.render();   // rewrite any mid-count figure to its final value
      };
    });
    if (typeof performance !== 'undefined' && performance.mark) performance.mark('bk-bb:motion:end');
  };

  /**
   * D-07: once-only fade-up of the How-it-works rows and the visible product cards.
   * Below 1024px only the first two grid rows take part; the rest render static.
   * Created inside the matchMedia context so a revert restores the CSS state.
   */
  P.setupReveals = function () {
    var g = window.gsap, ST = window.ScrollTrigger, m = this.motion;
    if (!ST || !m || !m.active) return;
    var hiw = qsa(this.root, '.bk-bb__hiw-item');
    var cards = qsa(this.root, '[data-card]:not([hidden])');
    if (window.innerWidth < 1024 && this.el.grid) {
      var cols = String(getComputedStyle(this.el.grid).gridTemplateColumns || '').split(' ').filter(Boolean).length || 2;
      cards = cards.slice(0, cols * 2);
    }
    var targets = hiw.concat(cards).filter(function (el) { return !el.__revealed; });
    if (!targets.length) return;
    g.set(targets, { opacity: 0, y: 16 });
    m.pending = targets;
    m.batch = ST.batch(targets, {
      start: 'top 85%',
      once: true,
      batchMax: 8,                               // a jump to the page end reveals in parallel batches, not one 1.6s stagger tail
      onEnter: function (batch) {
        batch.forEach(function (el) { el.__revealed = true; });
        g.to(batch, { opacity: 1, y: 0, duration: MOTION.reveal, ease: 'power2.out', stagger: MOTION.revealStagger, overwrite: true, clearProps: 'opacity,transform' });
        m.pending = m.pending.filter(function (el) { return !el.__revealed; });
        if (!m.pending.length) m.batch = null;   // every once-only trigger has fired and killed itself
      }
    });
  };

  /** A filter change re-lays the grid: show every card still waiting on its reveal, instantly. */
  P.revealNow = function () {
    var m = this.motion, g = window.gsap;
    if (!m || !m.pending.length) return;
    var rest = m.pending.filter(function (el) { return !el.__revealed; });
    (m.batch || []).forEach(function (st) { st.kill(); });   // D-11
    m.batch = null;
    m.pending = [];
    if (!rest.length) return;
    g.killTweensOf(rest);
    g.set(rest, { clearProps: 'opacity,transform' });
    rest.forEach(function (el) { el.__revealed = true; });
  };

  /** Debounced ScrollTrigger.refresh() — resize, and after stock batches change card heights. */
  P.motionRefresh = function () {
    var m = this.motion;
    if (!m || !m.active || !window.ScrollTrigger) return;
    clearTimeout(m.refreshTimer);
    m.refreshTimer = setTimeout(function () { window.ScrollTrigger.refresh(); }, MOTION.refreshDebounce);
  };

  /** Money figure: count to the new pence value (snap 1p) or write it instantly. */
  P.setMoney = function (el, pence, neg) {
    if (!el) return;
    var fmt = this.fmt;
    var write = function (v) { text(el, (neg && v ? '−' : '') + fmt.format(v / 100)); };
    var from = el.__mp ? el.__mp.v : el.__pence;
    el.__pence = pence;
    if (!this.motionOn() || from == null || from === pence) { if (el.__mp) window.gsap.killTweensOf(el.__mp); el.__mp = null; write(pence); return; }
    var g = window.gsap, m = this.motion;
    if (el.__mp) g.killTweensOf(el.__mp);
    var proxy = el.__mp = { v: from };
    m.track(g.to(proxy, {
      v: pence, duration: MOTION.money, ease: 'power2.out',
      onUpdate: function () { write(m.snap1(proxy.v)); },
      onComplete: function () { el.__mp = null; write(pence); }
    }));
  };

  /** Mobile bar percentage: count 10 → 15 with integer snap, or write instantly. */
  P.setPct = function (el, pct) {
    if (!el) return;
    var write = function (v) { text(el, v + '%'); };
    var from = el.__mp ? el.__mp.v : el.__pct;
    el.__pct = pct;
    if (!this.motionOn() || from == null || from === pct) { if (el.__mp) window.gsap.killTweensOf(el.__mp); el.__mp = null; write(pct); return; }
    var g = window.gsap, m = this.motion;
    if (el.__mp) g.killTweensOf(el.__mp);
    var proxy = el.__mp = { v: from };
    m.track(g.to(proxy, {
      v: pct, duration: MOTION.pct, ease: 'power2.out',
      onUpdate: function () { write(m.snap1(proxy.v)); },
      onComplete: function () { el.__mp = null; write(pct); }
    }));
  };

  /** Progress fill: scaleX tween (transform only) or an instant CSS-variable write. */
  P.setProgress = function (percent) {
    var self = this;
    var ratio = clamp(percent / 100, 0, 1);
    var fillEl = this.el.barFill;
    var writeVars = function () { self.root.style.setProperty('--bk-bb-progress', percent + '%'); self.root.style.setProperty('--bk-bb-progress-n', String(ratio)); };
    var prev = this.progressRatio;
    this.progressRatio = ratio;
    if (prev === ratio) { writeVars(); return; }   // same target: leave any running tween alone
    if (!fillEl || !this.motionOn() || prev == null) {
      writeVars();
      if (fillEl && window.gsap && fillEl.__tw) { window.gsap.killTweensOf(fillEl); fillEl.__tw = false; window.gsap.set(fillEl, { clearProps: 'transform' }); }
      return;
    }
    // Motion: GSAP reads the CURRENT scaleX (old variable, or the in-flight inline value) as its start,
    // so the variable is written only when the tween lands — writing it first would make the tween a no-op.
    var g = window.gsap;
    fillEl.__tw = true;
    this.motion.track(g.to(fillEl, { scaleX: ratio, duration: MOTION.fill, ease: 'power2.out', overwrite: true, transformOrigin: '0 50%', onComplete: function () { fillEl.__tw = false; writeVars(); g.set(fillEl, { clearProps: 'transform' }); } }));
  };

  /** Card badge single pulse 1 → 1.08 → 1 on add. */
  P.pulseBadge = function (variantId) {
    if (!this.motionOn()) return;
    var card = null;
    for (var i = 0; i < this.cards.length; i++) { if (this.cards[i].options.some(function (o) { return o.variantId === variantId; })) { card = this.cards[i]; break; } }
    var badge = card && card.el ? qs(card.el, '[data-badge]') : null;
    if (!badge || badge.hidden) return;
    var g = window.gsap, half = MOTION.badge / 2;
    g.killTweensOf(badge);
    this.motion.track(g.timeline()
      .to(badge, { scale: 1.08, duration: half, ease: 'power2.out' })
      .to(badge, { scale: 1, duration: half, ease: 'power2.out', clearProps: 'transform' }));
  };

  /** Summary row leaving: collapse height + opacity, then let renderItems reflow. */
  P.collapseRow = function (variantId) {
    if (!this.motionOn() || !this.el.items) return;
    this.finishCollapse();                       // a previous collapse rebuilds the list first — query the row after it
    var row = qs(this.el.items, '[data-item][data-variant="' + variantId + '"]');
    if (!row) return;
    var self = this, g = window.gsap, done = false;
    var finish = function () { if (done) return; done = true; clearTimeout(self.collapseTimer); self.collapseTimer = null; self.collapseTween = null; if (self.collapsing === row) { self.collapsing = null; self.renderItems(); } };
    this.collapseTimer = setTimeout(finish, MOTION.rowOut * 1000 + 100);   // safety net if the tween is killed
    this.collapsing = row;
    g.set(row, { overflow: 'hidden' });
    this.collapseTween = this.motion.track(g.to(row, { height: 0, opacity: 0, paddingTop: 0, paddingBottom: 0, marginBottom: -8, duration: MOTION.rowOut, ease: 'power2.in', overwrite: true, onComplete: finish }));
  };
  /** End a collapse early (another change, cleanup, teardown): kill its tween and timer, rebuild the list now. */
  P.finishCollapse = function () {
    if (this.collapseTween) { this.collapseTween.kill(); this.collapseTween = null; }
    clearTimeout(this.collapseTimer); this.collapseTimer = null;
    if (this.collapsing) { this.collapsing = null; this.renderItems(); }
  };

  /** Checkout busy state: opacity pulse, capped at 3s (0.5s × 6 half-cycles). */
  P.pulseBusy = function (busy) {
    var btn = this.el.checkout;
    if (!btn || !window.gsap) return;
    var g = window.gsap;
    g.killTweensOf(btn);
    if (!busy || !this.motionOn()) { g.set(btn, { clearProps: 'opacity' }); return; }
    this.motion.track(g.to(btn, { opacity: 0.45, duration: MOTION.busy, ease: 'sine.inOut', yoyo: true, repeat: 5, onComplete: function () { g.set(btn, { clearProps: 'opacity' }); } }));
  };

  /**
   * FAQ <details>: with motion on, the answer's height/opacity is tweened and the native
   * open attribute is flipped at the right end of the tween. aria-expanded on the summary
   * mirrors the open state in every mode via the toggle event.
   */
  P.bindFaq = function () {
    var self = this;
    qsa(this.root, '.bk-bb__faq-item').forEach(function (d) {
      var sum = qs(d, 'summary'), ans = qs(d, '.bk-bb__faq-a');
      if (!sum || !ans) return;
      var mirror = function () { sum.setAttribute('aria-expanded', d.open ? 'true' : 'false'); };
      mirror();
      on(d, 'toggle', mirror);
      on(sum, 'click', function (e) {
        if (!self.motionOn()) return;          // native behaviour, no tween
        e.preventDefault();
        if (d.__anim) return;
        var g = window.gsap;
        g.set(ans, { overflow: 'hidden' });
        if (!d.open) {
          d.open = true;
          d.__anim = self.motion.track(g.from(ans, { height: 0, paddingBottom: 0, opacity: 0, duration: MOTION.faq, ease: 'power2.out', clearProps: 'height,paddingBottom,opacity,overflow', onComplete: function () { d.__anim = null; } }));
        } else {
          d.classList.add('is-closing');
          sum.setAttribute('aria-expanded', 'false');
          d.__anim = self.motion.track(g.to(ans, { height: 0, paddingBottom: 0, opacity: 0, duration: MOTION.faq, ease: 'power2.in', onComplete: function () { d.open = false; d.classList.remove('is-closing'); g.set(ans, { clearProps: 'height,paddingBottom,opacity,overflow' }); d.__anim = null; } }));
        }
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                 */
  /* ------------------------------------------------------------------ */
  function boot(scope) {
    qsa(scope || document, '[data-bk-bundle-builder]').forEach(function (root) {
      if (root.__bkbb) return;
      root.__bkbb = new BundleBuilder(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    qsa(e.target, '[data-bk-bundle-builder]').forEach(function (root) {
      if (root.__bkbb) { root.__bkbb.destroy(); root.__bkbb = null; }
    });
  });

  // Exposed for tests / P5 QA only.
  window.BKBundleBuilder = { APPSTLE_KEYS: APPSTLE_KEYS, majorToPence: majorToPence, roundHalfDown: roundHalfDown, fill: fill, boot: boot };
})();
